import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Clock } from "lucide-react";
import reportApi from "../../../../api/reportApi";
import { useReportFilters } from "../../contexts/ReportFiltersContext";
import { formatDuration, formatDateTime } from "../../utils/format";
import EmptyState from "../../components/EmptyState";

const ConversationsTab = () => {
  const { shopId } = useReportFilters();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!shopId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    reportApi
      .getUnresolved({ shopId, limit: 200 })
      .then((res) => {
        if (cancelled) return;
        if (res.status === 1) setRows(res.data || []);
        else setError(res.message || "Không tải được dữ liệu");
      })
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [shopId]);

  const goToChat = (row) => {
    if (!shopId) return;
    navigate(`/chat/${shopId}?contact=${row.contactUid}`);
  };

  if (!shopId) {
    return (
      <div className="p-6">
        <EmptyState title="Chọn Zalo để xem báo cáo" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">
            Hội thoại đang kẹt
          </h2>
          <div className="text-xs text-gray-500">
            {rows.length} hội thoại chưa phản hồi
          </div>
        </div>
        {loading && <div className="p-6 text-sm text-gray-500">Đang tải...</div>}
        {error && <div className="p-6 text-sm text-red-500">Lỗi: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <EmptyState
            title="Không có hội thoại kẹt"
            description="Tất cả hội thoại đã được phản hồi"
          />
        )}
        {!loading && !error && rows.length > 0 && (
          <ul className="divide-y divide-gray-100">
            {rows.map((row) => {
              const isTimeout = row.status === "timeout";
              const waitStr = formatDuration(Math.round(row.minutesWaiting * 60));
              return (
                <li key={row.assignmentId}>
                  <button
                    type="button"
                    onClick={() => goToChat(row)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left"
                  >
                    {row.contactAvatar ? (
                      <img
                        src={row.contactAvatar}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-200 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {row.contactName || "—"}
                        </span>
                        {row.isGroup && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
                            Nhóm
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                        <Clock size={11} />
                        <span>Chờ {waitStr}</span>
                        <span>·</span>
                        <span>
                          Assign {formatDateTime(row.assignedAt)}
                        </span>
                        {row.staffName && (
                          <>
                            <span>·</span>
                            <span>{row.staffName}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div
                      className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                        isTimeout
                          ? "bg-red-100 text-red-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {isTimeout && <AlertTriangle size={12} />}
                      {isTimeout ? "Quá SLA" : "Đang chờ"}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ConversationsTab;
